(() => {
  class AspadzProfessionalReviews extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.retryTimer = null;
      this.activeGroup = null;
      this.activeVideoTrigger = null;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.onClick = this.onClick.bind(this);
      this.onControlChange = this.onControlChange.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.handleMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.onModalCancel = this.onModalCancel.bind(this);
      this.onCarouselMouseEnter = this.onCarouselMouseEnter.bind(this);
      this.onVideoPointerDown = this.onVideoPointerDown.bind(this);
      this.onVideoPointerMove = this.onVideoPointerMove.bind(this);
    }

    connectedCallback() {
      this.modal = this.querySelector('[data-aspadz-professional-reviews-modal]');
      this.slider = this.querySelector('[data-aspadz-professional-reviews-slider]');
      this.activeGroup = this.getInitialGroup();

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onControlChange);
      this.addEventListener('pointerdown', this.onVideoPointerDown);
      document.addEventListener('pointermove', this.onVideoPointerMove, { passive: true });
      document.addEventListener('pointerup', this.onVideoPointerMove, { passive: true });
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      this.modal?.addEventListener('cancel', this.onModalCancel);
      this.slider?.addEventListener('mouseenter', this.onCarouselMouseEnter);
      this.slider?.addEventListener('mouseleave', this.handleMotionPreferenceChange);
      this.addQueryListener(this.motionQuery, this.handleMotionPreferenceChange);
      this.addQueryListener(this.mobileQuery, this.handleViewportChange);
      this.syncControls();
      this.applySlideVisibility();
      this.init();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onControlChange);
      this.removeEventListener('pointerdown', this.onVideoPointerDown);
      document.removeEventListener('pointermove', this.onVideoPointerMove);
      document.removeEventListener('pointerup', this.onVideoPointerMove);
      this.videoPointerGesture = null;
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      this.modal?.removeEventListener('cancel', this.onModalCancel);
      this.slider?.removeEventListener('mouseenter', this.onCarouselMouseEnter);
      this.slider?.removeEventListener('mouseleave', this.handleMotionPreferenceChange);
      this.removeQueryListener(this.motionQuery, this.handleMotionPreferenceChange);
      this.removeQueryListener(this.mobileQuery, this.handleViewportChange);
      window.clearTimeout(this.retryTimer);
      this.closeVideoModal(false);
      this.destroySwiper();
    }

    addQueryListener(query, callback) {
      if (query.addEventListener) {
        query.addEventListener('change', callback);
      } else {
        query.addListener(callback);
      }
    }

    removeQueryListener(query, callback) {
      if (query.removeEventListener) {
        query.removeEventListener('change', callback);
      } else {
        query.removeListener(callback);
      }
    }

    init() {
      if (this.swiper) return;

      if (typeof window.AspadzConceptCarousel === 'undefined') {
        this.retryTimer = window.setTimeout(() => this.init(), 120);
        return;
      }

      const slider = this.querySelector('[data-aspadz-professional-reviews-slider]');
      const slides = this.getVisibleSlides();
      this.classList.toggle('aspadz-professional-reviews--single-view', slides.length < 2);
      if (!slider || slides.length < 2) return;

      const delay = Number(this.dataset.autoplayDelay) || 5000;
      this.style.setProperty('--aspadz-professional-reviews-delay', `${delay}ms`);

      this.swiper = new window.AspadzConceptCarousel(slider, {
        // Exclude the other review category from cells, indices and pagination.
        cellSelector: '.swiper-slide:not([hidden])',
        autoplay: this.motionQuery.matches
          ? false
          : {
              delay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            },
        pagination: {
          clickable: true,
          el: this.querySelector('[data-aspadz-professional-reviews-pagination]'),
        },
        rewind: true,
        slidesPerView: 'auto',
        spaceBetween: 16,
        speed: 650,
        watchOverflow: true,
        breakpoints: {
          750: {
            spaceBetween: 20,
          },
        },
        on: {
          init: () => this.restartProgress(),
          slideChangeTransitionStart: () => this.restartProgress(),
          autoplayResume: () => this.restartProgress(),
        },
      });

      // Flickity stops its player on pointerDown, even when disableOnInteraction is false.
      this.swiper.instance.on('pointerDown', () => this.classList.remove('is-progressing'));
      this.swiper.instance.on('pointerUp', () => this.handleMotionPreferenceChange());
      this.handleMotionPreferenceChange();
    }

    destroySwiper() {
      if (!this.swiper) return;

      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    getSlides() {
      return Array.from(this.querySelectorAll('[data-aspadz-professional-reviews-slide]'));
    }

    getVisibleSlides() {
      return this.getSlides().filter((slide) => !slide.hidden);
    }

    getInitialGroup() {
      const select = this.querySelector('[data-aspadz-professional-reviews-select]');
      if (select?.value) return select.value;

      const activeOption = this.querySelector('[data-aspadz-professional-reviews-dropdown-option].is-active');
      if (activeOption?.dataset.group) return activeOption.dataset.group;

      const activeTab = this.querySelector('[data-aspadz-professional-reviews-tab][aria-pressed="true"]');
      if (activeTab?.dataset.group) return activeTab.dataset.group;

      const firstTab = this.querySelector('[data-aspadz-professional-reviews-tab]');
      return firstTab?.dataset.group || null;
    }

    onClick(event) {
      // Flickity cancels the click that follows a drag. Delegated handlers must respect it.
      if (event.defaultPrevented) return;

      const dropdownButton = event.target.closest('[data-aspadz-professional-reviews-dropdown-button]');
      if (dropdownButton && this.contains(dropdownButton)) {
        event.preventDefault();
        this.toggleDropdown();
        return;
      }

      const dropdownOption = event.target.closest('[data-aspadz-professional-reviews-dropdown-option]');
      if (dropdownOption && this.contains(dropdownOption)) {
        event.preventDefault();
        this.activateGroup(dropdownOption.dataset.group);
        this.closeDropdown();
        return;
      }

      const videoTrigger = event.target.closest('[data-aspadz-professional-reviews-video-trigger]');
      if (videoTrigger && this.contains(videoTrigger)) {
        event.preventDefault();
        if (event.detail > 0 && (this.videoPointerGesture?.moved || this.swiper?.instance?.isPreventingClicks)) return;
        this.openVideoModal(videoTrigger);
        return;
      }

      const closeTrigger = event.target.closest('[data-aspadz-professional-reviews-modal-close]');
      if (closeTrigger && this.contains(closeTrigger)) {
        event.preventDefault();
        this.closeVideoModal(true);
        return;
      }

      const tab = event.target.closest('[data-aspadz-professional-reviews-tab]');
      if (!tab || !this.contains(tab)) return;

      this.activateGroup(tab.dataset.group);
    }

    onControlChange(event) {
      const select = event.target.closest('[data-aspadz-professional-reviews-select]');
      if (!select || !this.contains(select)) return;

      this.activateGroup(select.value);
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.closeDropdown();
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      const modal = this.querySelector('[data-aspadz-professional-reviews-modal]:not([hidden])');
      if (modal) {
        event.preventDefault();
        this.closeVideoModal(true);
        return;
      }

      this.closeDropdown();
    }

    onModalCancel(event) {
      event.preventDefault();
      this.closeVideoModal(true);
    }

    onCarouselMouseEnter() {
      this.classList.remove('is-progressing');
    }

    onVideoPointerDown(event) {
      const trigger = event.target.closest('[data-aspadz-professional-reviews-video-trigger]');
      if (!trigger || !this.contains(trigger) || !event.isPrimary || event.button !== 0) return;

      this.videoPointerGesture = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
    }

    onVideoPointerMove(event) {
      const gesture = this.videoPointerGesture;
      if (!gesture || gesture.finished || event.pointerId !== gesture.id) return;

      // Keep the furthest movement, including a drag back to the start or a single-card gallery.
      if (Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 8) gesture.moved = true;
      if (event.type === 'pointerup') gesture.finished = true;
    }

    activateGroup(group) {
      if (!group) return;

      this.activeGroup = group;
      this.syncControls();
      this.applySlideVisibility();
      this.rebuildSwiper();
    }

    syncControls() {
      const select = this.querySelector('[data-aspadz-professional-reviews-select]');
      if (select && Array.from(select.options).some((option) => option.value === this.activeGroup)) {
        select.value = this.activeGroup;
      }

      this.querySelectorAll('[data-aspadz-professional-reviews-tab]').forEach((item) => {
        item.setAttribute('aria-pressed', String(item.dataset.group === this.activeGroup));
      });

      const activeDropdownOption = this.querySelector(`[data-aspadz-professional-reviews-dropdown-option][data-group="${this.activeGroup}"]`);
      const dropdownLabel = this.querySelector('[data-aspadz-professional-reviews-dropdown-label]');
      if (dropdownLabel && activeDropdownOption) {
        dropdownLabel.textContent = activeDropdownOption.textContent.trim();
      }

      this.querySelectorAll('[data-aspadz-professional-reviews-dropdown-option]').forEach((option) => {
        const isActive = option.dataset.group === this.activeGroup;
        option.classList.toggle('is-active', isActive);
        option.setAttribute('aria-selected', String(isActive));
      });
    }

    toggleDropdown(force) {
      const button = this.querySelector('[data-aspadz-professional-reviews-dropdown-button]');
      const menu = this.querySelector('[data-aspadz-professional-reviews-dropdown-menu]');
      if (!button || !menu) return;

      const shouldOpen = typeof force === 'boolean' ? force : menu.hidden;
      menu.hidden = !shouldOpen;
      button.setAttribute('aria-expanded', String(shouldOpen));
    }

    closeDropdown() {
      this.toggleDropdown(false);
    }

    handleViewportChange() {
      this.applySlideVisibility();
      this.rebuildSwiper();
    }

    applySlideVisibility() {
      const shouldFilter = Boolean(this.activeGroup);
      let visibleCount = 0;

      this.getSlides().forEach((slide) => {
        const slideGroup = slide.dataset.group || 'industry';
        const isVisible = !shouldFilter || slideGroup === this.activeGroup;
        slide.hidden = !isVisible;
        if (isVisible) visibleCount += 1;
      });

      if (visibleCount === 0) {
        this.getSlides().forEach((slide) => {
          slide.hidden = false;
        });
        visibleCount = this.getSlides().length;
      }

      this.classList.toggle('aspadz-professional-reviews--single-view', visibleCount < 2);
    }

    rebuildSwiper() {
      this.destroySwiper();
      this.init();
    }

    restartProgress() {
      this.classList.remove('is-progressing');
      window.requestAnimationFrame(() => {
        if (!this.motionQuery.matches && !this.modal?.open && this.swiper?.instance?.player?.state === 'playing') {
          this.classList.add('is-progressing');
        }
      });
    }

    handleMotionPreferenceChange() {
      const reduceMotion = this.motionQuery.matches;
      this.classList.toggle('aspadz-professional-reviews--reduced-motion', reduceMotion);

      if (!this.swiper?.autoplay) {
        this.classList.remove('is-progressing');
        return;
      }

      if (reduceMotion || this.modal?.open || !this.isConnected) {
        this.swiper.autoplay.stop();
        this.classList.remove('is-progressing');
      } else {
        this.swiper.autoplay.start();
        if (window.matchMedia('(hover: hover)').matches && this.slider?.matches(':hover')) {
          this.swiper.autoplay.pause();
          this.classList.remove('is-progressing');
        } else {
          this.restartProgress();
        }
      }
    }

    openVideoModal(trigger) {
      const modal = this.querySelector('[data-aspadz-professional-reviews-modal]');
      const frame = modal?.querySelector('[data-aspadz-professional-reviews-video-frame]');
      const videoUrl = trigger.dataset.videoUrl;
      if (!modal || !frame || !videoUrl || modal.open) return;

      this.activeVideoTrigger = trigger;
      this.renderVideo(frame, videoUrl, trigger.getAttribute('aria-label') || 'Review video');
      modal.hidden = false;
      // The native top layer escapes section stacking contexts and traps keyboard focus.
      modal.showModal();
      // Native dialogs make the background inert; preserve scroll position and sticky-header layout.
      window.AspadzScrollLock?.lock(modal, { preserveLayout: true });
      document.documentElement.classList.add('aspadz-product-guided-tour-modal-open');
      this.swiper?.autoplay?.stop();
      this.classList.remove('is-progressing');

      const closeButton = modal.querySelector('.aspadz-product-guided-tour__modal-close');
      if (closeButton && typeof closeButton.focus === 'function') {
        closeButton.focus({ preventScroll: true });
      }
    }

    closeVideoModal(restoreFocus = true) {
      const modal = this.querySelector('[data-aspadz-professional-reviews-modal]');
      if (!modal || modal.hidden) return;

      const frame = modal.querySelector('[data-aspadz-professional-reviews-video-frame]');
      if (frame) frame.replaceChildren();

      if (modal.open) modal.close();
      modal.hidden = true;

      const hasOpenModal = document.querySelector('[data-aspadz-professional-reviews-modal]:not([hidden]), [data-aspadz-guided-tour-popup]:not([hidden])');
      if (!hasOpenModal) {
        document.documentElement.classList.remove('aspadz-product-guided-tour-modal-open');
      }
      window.AspadzScrollLock?.unlock(modal);

      if (restoreFocus && this.activeVideoTrigger && typeof this.activeVideoTrigger.focus === 'function') {
        this.activeVideoTrigger.focus({ preventScroll: true });
      }

      this.activeVideoTrigger = null;
      this.handleMotionPreferenceChange();
    }

    renderVideo(frame, videoUrl, title) {
      frame.replaceChildren();

      const source = this.getVideoSource(videoUrl);
      if (!source) return;

      if (source.type === 'video') {
        const video = document.createElement('video');
        video.src = source.src;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        frame.append(video);
        video.play?.().catch(() => {});
        return;
      }

      const iframe = document.createElement('iframe');
      iframe.src = source.src;
      iframe.title = title;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      frame.append(iframe);
    }

    getVideoSource(videoUrl) {
      let parsedUrl;

      try {
        parsedUrl = new URL(videoUrl, window.location.href);
      } catch (error) {
        return null;
      }

      const host = parsedUrl.hostname.replace(/^www\./, '').replace(/^m\./, '');
      const path = parsedUrl.pathname.replace(/^\/+/, '');
      const videoExtension = /\.(mp4|webm|ogg|ogv|mov)$/i;

      if (host === 'youtu.be') {
        const id = path.split('/')[0];
        return id ? { type: 'iframe', src: this.getYouTubeEmbedUrl(id, parsedUrl) } : null;
      }

      if (host === 'youtube.com' || host.endsWith('.youtube.com')) {
        let id = parsedUrl.searchParams.get('v');

        if (!id && (path.startsWith('embed/') || path.startsWith('shorts/') || path.startsWith('live/'))) {
          id = path.split('/')[1];
        }

        return id ? { type: 'iframe', src: this.getYouTubeEmbedUrl(id, parsedUrl) } : null;
      }

      if (host === 'vimeo.com' || host.endsWith('.vimeo.com')) {
        const id = path.split('/').find((part) => /^\d+$/.test(part));
        return id ? { type: 'iframe', src: `https://player.vimeo.com/video/${id}?autoplay=1` } : { type: 'iframe', src: parsedUrl.href };
      }

      if (videoExtension.test(parsedUrl.pathname)) {
        return { type: 'video', src: parsedUrl.href };
      }

      return { type: 'iframe', src: parsedUrl.href };
    }

    getYouTubeEmbedUrl(id, parsedUrl) {
      const params = new URLSearchParams({
        autoplay: '1',
        playsinline: '1',
        rel: '0',
      });
      const start = this.getYouTubeStartTime(parsedUrl.searchParams.get('start') || parsedUrl.searchParams.get('t'));

      if (start) params.set('start', start);

      return `https://www.youtube.com/embed/${encodeURIComponent(id)}?${params.toString()}`;
    }

    getYouTubeStartTime(value) {
      if (!value) return '';
      if (/^\d+$/.test(value)) return value;

      const hours = value.match(/(\d+)h/);
      const minutes = value.match(/(\d+)m/);
      const seconds = value.match(/(\d+)s/);
      const total = (hours ? Number(hours[1]) * 3600 : 0) + (minutes ? Number(minutes[1]) * 60 : 0) + (seconds ? Number(seconds[1]) : 0);

      return total ? String(total) : '';
    }
  }

  if (!window.customElements.get('aspadz-professional-reviews')) {
    window.customElements.define('aspadz-professional-reviews', AspadzProfessionalReviews);
  }
})();
